import { useCallback, useEffect, useState } from 'react';
import { CallFrame } from '../components/CallFrame';
import { Button, Card, Chip, EmptyState, Heading, Loading, Row, TextField } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { formatPhone, waMeLink } from '../lib/format';
import type { CustomerSummary } from './Customers';
import './cartcall.css';

/**
 * The cart's call experience (§6): starting a Bulawa, and being in a call.
 *
 * Everything the cart must SEE without refreshing — a guest arriving, a Bulawa
 * declined — arrives on the 2-second poll owned by the parent screen (§0.5).
 */

export interface LiveGuest {
  customer_id: string;
  name: string;
  phone: string;
  joined_at: string;
  left_at: string | null;
}

export interface Live {
  call_id: string;
  type: 'nyauta' | 'bulawa';
  started_at: string;
  staff_name: string;
  guests: LiveGuest[];
  bulawa: { declined: boolean; declined_at: string | null; opened: boolean; expired: boolean } | null;
}

export interface StartedCall {
  id: string;
  type: 'nyauta' | 'bulawa';
  room_url: string;
  token: string;
  customer_name: string;
}

/** What the cart shows while a call is running. */
export function LiveCall({
  live,
  started,
  shareLink,
  sharePhone,
  shareName,
  onEnded,
}: {
  live: Live;
  started: StartedCall | null;
  shareLink: string | null;
  sharePhone: string | null;
  shareName: string | null;
  onEnded: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function end() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/calls/${live.call_id}/end`, { method: 'POST' });
      onEnded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not end the call.');
    } finally {
      setBusy(false);
    }
  }

  const here = live.guests.filter((g) => !g.left_at);
  const isStub = started?.room_url.includes('mulaqaat-stub.invalid');

  const message =
    shareName && shareLink
      ? `Sat Sri Akal ${shareName} ji 🙏\n\nArjun Jewellers ton Bulawa — we are ready to show you something now.\n\n${shareLink}`
      : '';

  return (
    <>
      {/* The declined banner is the whole point of the 2-second poll (§0.5). */}
      {live.bulawa?.declined ? (
        <Card className="cc__declined">
          <Heading level={3}>They cannot talk right now</Heading>
          <p className="cc__sub">
            {shareName ?? 'The customer'} tapped “I can’t right now”. End the call and try again later.
          </p>
          <Button variant="danger" onClick={() => void end()} busy={busy}>
            End the Mulaqaat
          </Button>
        </Card>
      ) : null}

      <Card>
        <Row className="cc__head">
          <Row>
            <Chip tone="live">Live</Chip>
            <Chip tone="neutral">{live.type === 'bulawa' ? 'Bulawa' : 'Booked'}</Chip>
            <span className="cc__who">{started?.customer_name ?? shareName ?? ''}</span>
          </Row>
          <Button variant="danger" onClick={() => void end()} busy={busy}>
            End the Mulaqaat
          </Button>
        </Row>

        {error ? (
          <p className="field__error" role="alert">
            {error}
          </p>
        ) : null}

        {started && !isStub ? (
          <CallFrame roomUrl={started.room_url} token={started.token} />
        ) : (
          <div className="cc__stub">
            Video is in test mode on this server, so no picture appears here. Starting, joining, declining
            and ending are all real.
          </div>
        )}
      </Card>

      <Card>
        <Heading level={3}>In the room</Heading>
        {here.length === 0 ? (
          <EmptyState
            line={
              live.bulawa?.declined
                ? 'They declined.'
                : live.bulawa?.opened
                  ? 'They have opened the link — waiting for them to join.'
                  : 'Waiting for them to join. They will knock and you let them in.'
            }
          />
        ) : (
          <ul className="cc__guests">
            {here.map((g) => (
              <li key={g.customer_id} className="cc__guest">
                <span className="cc__gname">{g.name}</span>
                <span className="cc__gphone">{formatPhone(g.phone)}</span>
                <Chip tone="live">Here</Chip>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {live.type === 'bulawa' && shareLink && !live.bulawa?.declined ? (
        <Card>
          <Heading level={3}>Send the Bulawa</Heading>
          <Row className="cc__actions">
            {sharePhone ? (
              <a
                className="btn btn--primary"
                href={waMeLink(sharePhone, message)}
                target="_blank"
                rel="noreferrer noopener"
              >
                Send on WhatsApp
              </a>
            ) : null}
            {live.bulawa?.expired ? <Chip tone="urgent">The link has expired</Chip> : null}
          </Row>
          <p className="cc__link">{shareLink}</p>
        </Card>
      ) : null}
    </>
  );
}

/** Start a walk-in Bulawa, with the clash warning first (§6). */
export function StartBulawa({
  onStarted,
  onCancel,
}: {
  onStarted: (started: StartedCall, token: string, customer: { name: string; phone: string }) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<CustomerSummary[] | null>(null);
  const [picked, setPicked] = useState<CustomerSummary | null>(null);
  const [clash, setClash] = useState<{ customer_name: string; minutes_away: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await api<{ customers: CustomerSummary[] }>(
          `/api/customers?q=${encodeURIComponent(q)}&limit=20`
        );
        setRows(res.customers);
      } catch {
        setRows([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  // Ask about a clash as soon as somebody is picked, so the warning appears
  // BEFORE the call is started, not after (§6).
  useEffect(() => {
    if (!picked) return;
    void (async () => {
      try {
        const res = await api<{ clash: { customer_name: string; minutes_away: number } | null }>(
          '/api/calls/clash'
        );
        setClash(res.clash);
      } catch {
        setClash(null);
      }
    })();
  }, [picked]);

  async function start() {
    if (busy || !picked) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ call: StartedCall; bulawa_token: string; customer: { name: string; phone: string } }>(
        '/api/calls/start-bulawa',
        { method: 'POST', body: { customer_id: picked.id } }
      );
      onStarted(res.call, res.bulawa_token, res.customer);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the Bulawa.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Row className="cc__head">
        <Heading>Start a Bulawa</Heading>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </Row>

      {picked ? (
        <>
          <Row className="cc__picked">
            <div>
              <strong>{picked.name}</strong>
              <span className="cc__sub">{formatPhone(picked.phone)}</span>
            </div>
            <Button variant="ghost" onClick={() => { setPicked(null); setClash(null); }}>
              Change
            </Button>
          </Row>

          {/* Amber, with the minutes remaining — and it does not block (§6). */}
          {clash ? (
            <div className="cc__clash" role="alert">
              <strong>{clash.customer_name} is booked in {clash.minutes_away} minutes.</strong>
              <span>
                A Bulawa now may run into that appointment. You can still go ahead.
              </span>
            </div>
          ) : null}

          {error ? (
            <p className="field__error" role="alert">
              {error}
            </p>
          ) : null}

          <Button full busy={busy} onClick={() => void start()}>
            {busy ? 'Starting…' : `Start the Bulawa for ${picked.name}`}
          </Button>
        </>
      ) : (
        <>
          <TextField
            label="Who are we calling?"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name or number"
            autoComplete="off"
          />
          {!rows ? (
            <Loading label="Searching…" />
          ) : rows.length === 0 ? (
            <EmptyState line="Nobody matches. Add them from a phone first." />
          ) : (
            <ul className="picker__list">
              {rows.map((r) => (
                <li key={r.id}>
                  <button type="button" className="picker__item" onClick={() => setPicked(r)}>
                    <span>{r.name}</span>
                    <span className="picker__phone">{formatPhone(r.phone)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** Starting a booked call from the cart's day (§6). */
export function useStartBooked() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async (bookingId: string): Promise<StartedCall | null> => {
    setBusy(bookingId);
    setError(null);
    try {
      const res = await api<{ call: StartedCall }>('/api/calls/start-booked', {
        method: 'POST',
        body: { booking_id: bookingId },
      });
      return res.call;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start that call.');
      return null;
    } finally {
      setBusy(null);
    }
  }, []);

  return { start, busy, error };
}
